import React from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { ExternalLink, DollarSign, Clock, CheckCircle2 } from 'lucide-react';

const CPCOffers = () => {
  const cpcOffers = [
    {
      id: 1,
      title: "Compare Credit Cards - Get Best Rates",
      description: "Find the perfect credit card with our free comparison tool. See rates, rewards, and benefits instantly.",
      payout: "$15-30 per click",
      action: "Get Free Comparison",
      category: "Finance",
      time: "2 minutes",
      image: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=400&q=80"
    },
    {
      id: 2,
      title: "Free Auto Insurance Quote",
      description: "Compare rates from top insurers. Save up to $500/year on auto insurance. No obligation quote in 60 seconds.",
      payout: "$10-25 per click",
      action: "Get Free Quote",
      category: "Insurance",
      time: "1 minute",
      image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=400&q=80"
    },
    {
      id: 3,
      title: "Web Hosting - Free Trial",
      description: "Start your website today with industry-leading hosting. Free domain, SSL certificate, and 24/7 support included.",
      payout: "$5-15 per click",
      action: "Start Free Trial",
      category: "Web Services",
      time: "3 minutes",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&q=80"
    },
    {
      id: 4,
      title: "Personal Loan - Check Your Rate",
      description: "Get pre-qualified for a personal loan up to $50,000. Check your rate without affecting credit score.",
      payout: "$20-40 per click",
      action: "Check My Rate",
      category: "Finance",
      time: "2 minutes",
      image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&q=80"
    }
  ];

  const handleCPCClick = (offer) => {
    // Track click
    console.log('CPC offer clicked:', offer.title);
    
    // In production, this opens your CPC affiliate link
    // For now, shows alert
    alert(`Opening ${offer.title}\n\nYou earn: ${offer.payout} per click!\n\nNote: Add your real CPC affiliate links to make this live.`);
  };

  return (
    <section className="py-20 px-4 bg-white">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <DollarSign className="w-4 h-4" />
            <span>Get Paid Per Click - No Purchase Required</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Free Tools & Resources
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Check rates, get quotes, and access free trials. Quick and easy - most take under 3 minutes.
          </p>
        </div>

        {/* CPC Offers Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {cpcOffers.map((offer) => (
            <Card key={offer.id} className="overflow-hidden hover:shadow-xl transition-shadow">
              <div className="relative">
                <img 
                  src={offer.image} 
                  alt={offer.title}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  {offer.category}
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {offer.title}
                </h3>
                
                <p className="text-gray-600 mb-4">
                  {offer.description}
                </p>

                <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span>{offer.time}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>Free - No obligation</span>
                  </div>
                </div>

                <Button 
                  onClick={() => handleCPCClick(offer)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6"
                >
                  {offer.action}
                  <ExternalLink className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Info Box */}
        <Card className="p-8 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            💡 Why These Are Helpful
          </h3>
          <div className="grid md:grid-cols-2 gap-6 text-gray-700">
            <div>
              <h4 className="font-semibold mb-2">Compare & Save Money</h4>
              <p className="text-sm">These free tools help you find better rates and save money on essential services.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Quick & Easy</h4>
              <p className="text-sm">Most comparisons take under 3 minutes. No obligation to buy anything.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Trusted Providers</h4>
              <p className="text-sm">All offers are from established, reputable companies with strong reviews.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Free to Use</h4>
              <p className="text-sm">100% free comparisons. See your options without spending a penny.</p>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};
export default CPCOffers;
